import React, { useState } from "react";
import { Box } from "@mui/system";
import {
  Grid,
  Step,
  StepIconProps,
  Stepper,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
  Skeleton,
} from "@mui/material";
import {
  Check,
  Close,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from "@mui/icons-material";

// --- EXPORTED TYPES ---
export interface StepData {
  label: string;
  // completionRate: number; // Removed
  status?: "pending" | "completed" | "inprogress";
  isEligible?: boolean;
  isProvisionalGenerated?: boolean;
  isQualified?: boolean;
  applicationStatus?: string;
}
export interface ApplicationProgressContainerProps {
  data: StepData[];
  activeStep: number;
  isLoading: boolean;
  partnerStartedJourney?: boolean;
}

// --- Internal Component Types ---
interface CustomStepIconProps extends StepIconProps {
  totalSteps: number;
}

const StepIconRoot = styled(Box)<{
  isactive: "true" | "false";
  iscompleted: "true" | "false";
}>(({ isactive, iscompleted }) => ({
  width: 35,
  height: 35,
  display: "flex",
  borderRadius: "50%",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 600,
  fontSize: "1rem",
  color: isactive === "true" || iscompleted === "true" ? "#fff" : "#66646dff",
  backgroundColor:
    isactive === "true" || iscompleted === "true" ? "#00B3B5" : "#F5F5F5",
  zIndex: 3,
  flexShrink: 0,
}));

const StepWrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  flex: "1 1 0%",
  minWidth: 0,
  position: "relative",
  "&:last-child": {
    "& > .progress-bar-line": {
      display: "none",
    },
  },
});

const ProgressBarContainer = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  position: "relative",
  width: "100%",
});

const ProgressBarLine = styled(Box)<{ iscompleted: "true" | "false" }>(
  ({ iscompleted }) => ({
    position: "absolute",
    top: "15px",
    left: "30px",
    right: 0,
    height: "3px",
    borderRadius: "4px",
    backgroundColor: iscompleted === "true" ? "#00B3B5" : "#E0E0E0",
    zIndex: 1,
    marginLeft: 15,
    marginRight: 15,
  })
);

// Custom Step Icon logic for the new desktop stepper
const CustomStepIconNew: React.FC<{
  index: number;
  isCompleted: boolean;
  isActive: boolean;
  isEligible?: boolean | null;
  isQualified?: boolean;
  appStatus?: string;
}> = ({ index, isCompleted, isActive, isEligible, isQualified, appStatus }) => {
  // Show cross if it's the first step and isEligible is false
  const showCross = index === 0 && isEligible === false;
  
  const iconBgColor = showCross ? "#f44336" : ((isActive || isCompleted) && (appStatus !== "application closed" && appStatus !== "application rejected" && appStatus !== "declined by credit") ? "#00B3B5" : "#F5F5F5");
  const iconColor = ((showCross || isActive || isCompleted) && (appStatus !== "application closed" && appStatus !== "application rejected" && appStatus !== "declined by credit")) ? "#fff" : "#66646dff";
  
  return (
    <StepIconRoot
      isactive={isActive && appStatus !== "application closed" && appStatus !== "application rejected" && appStatus !== "declined by credit" ? "true" : "false"}
      iscompleted={isCompleted && appStatus !== "application closed" && appStatus !== "application rejected" && appStatus !== "declined by credit" ? "true" : "false"}
      sx={{
        backgroundColor: iconBgColor,
        color: iconColor,
      }}
    >
      {showCross ? (
        <Close sx={{ fontSize: "1.2rem" }} />
      ) : index === 0 && !isCompleted  && (appStatus !== "application closed" && appStatus !== "application rejected" && appStatus !== "declined by credit") ? (
        // Always show "1" for first step when not completed
        1
      ) : isCompleted ? (
        <Check sx={{ fontSize: "1.2rem" }} />
      ) : (
        index + 1
      )}
    </StepIconRoot>
  );
};

// --- Custom Step Icon (Used by Mobile Accordion) ---
const CustomStepIcon: React.FC<CustomStepIconProps & { isQualified?: boolean; isEligible?: boolean | null; appStatus?: string }> = (props) => {
  const { icon, active, completed, isEligible, appStatus } = props;
  
  // Show cross if it's the first step (icon === 1) and isEligible is false
  const showCross = icon === 1 && isEligible === false;
  const iconBgColor = showCross ? "#f44336" : (completed || active) && (appStatus !== "application closed" && appStatus !== "application rejected" && appStatus !== "declined by credit") ? "rgb(0, 179, 181)" : "#e0e0e0";
  return (
    <Box
      sx={{
        width: 30,
        height: 30,
        borderRadius: "50%",
        bgcolor: iconBgColor,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: "bold",
        fontSize: "0.9rem",
        color: "#fff",
        flexShrink: 0,
      }}
    >
      {showCross ? (
        <Close sx={{ color: "#fff", fontSize: "1rem" }} />
      ) : icon === 1 && !completed  && (appStatus !== "application closed" && appStatus !== "application rejected" && appStatus !== "declined by credit") ? (
        // Show "1" for first step when not completed
        1
      ) : completed ? (
        <Check sx={{ color: "#fff" }} />
      ) : (
        icon
      )}
    </Box>
  );
};

// --- Desktop Stepper Skeleton (Horizontal - KEPT) ---
const DesktopStepperSkeleton: React.FC = () => (
  <Grid item xs={12} md={12}>
    <Typography className="card-section-hedging">
      <Skeleton variant="text" width={200} height={30} />
    </Typography>

    <Skeleton height={2} width="100%" sx={{ my: 2 }} />

    <Stepper
      activeStep={0}
      connector={
        <Skeleton
          variant="text"
          width={50}
          height={5}
          sx={{ mx: 1, bgcolor: "#e0e0e0" }}
        />
      }
      sx={{ mb: 2, display: "flex" }}
    >
      {Array.from({ length: 6 }).map((_, index) => (
        <Step key={index}>
          <Box display="flex" flexDirection={"column"} alignItems="flex-start">
            <Skeleton
              variant="rectangular"
              width={30}
              height={30}
              sx={{ borderRadius: "6px", flexShrink: 0 }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                mt: 1,
              }}
            >
              <Skeleton variant="text" sx={{ fontSize: "0.7rem" }} width={90} />
              <Skeleton variant="text" sx={{ fontSize: "0.6rem" }} width={70} />
            </Box>
          </Box>
        </Step>
      ))}
    </Stepper>
  </Grid>
);

// --- Mobile Accordion Skeleton (Vertical/Stacked - KEPT) ---
const MobileAccordionSkeleton: React.FC = () => {
  return (
    <Box
      sx={{
        p: 1,
        border: "1px solid rgba(215, 216, 222, 1)",
        borderRadius: 2,
        mb: 2,
        mt: 2,
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Skeleton variant="text" width={150} height={25} />
        <KeyboardArrowDownIcon sx={{ color: "#00b3b5" }} />
      </Box>
    </Box>
  );
};

// --- Desktop Stepper Component ---
const DesktopStepper: React.FC<ApplicationProgressContainerProps> = ({
  data,
  partnerStartedJourney,
}) => {
  const steps = data;
  const totalSteps = steps.length;


  // Check if first step is not eligible (false) or in-progress (null)
  const isFirstStepNotEligible = steps[0]?.isEligible === false;
  const isFirstStepInProgress = steps[0]?.isEligible === null;
  
  // Check if first step is qualified and eligibility check is null/undefined (should show tick and move to next stage)
  const isFirstStepQualifiedAndChecking = (steps[0]?.isEligible === null || steps[0]?.isEligible === undefined) && steps[0]?.isQualified === true;
  // Check if first step is not qualified and eligibility check is null/undefined (should show "1" and stay on first stage)
  const isFirstStepNotQualifiedAndChecking = steps[0]?.isQualified === false && (steps[0]?.isEligible === null || steps[0]?.isEligible === undefined);
  
  // Check if first step is completed (ticked) - MUST match the isCompleted logic for index 0
  const isFirstStepCompleted = (() => {
    const step = steps[0];
    if (!step) return false;
    // If eligibility is checked, always completed
    if (step.isEligible === true) return true;
    // If qualified and not checked, completed
    if ((step.isEligible === null || step.isEligible === undefined) && step.isQualified === true) return true;
    // Not qualified and not checked
    if (step.isQualified === false && (step.isEligible === null || step.isEligible === undefined)) return false;
    // Eligibility pending
    if (step.isEligible === null) return false;
    // Not eligible
    if (step.isEligible === false) return false;
    // Default: not completed
    return false;
  })();
  // Determine the active step index
  let activeStepIndex = 0;
  // If first step is completed (ticked), check if we should move to next stage
  // Also, if isEligible === true, second stage should be in progress
  if (isFirstStepCompleted) {
    // If isEligible === true, set activeStepIndex to 1 (second stage in progress)
    if (steps[0]?.isEligible === true) {
      activeStepIndex = 1;
    } else {
      // Find the first incomplete step starting from index 1
      const nextIncompleteStep = steps.findIndex((step, index) => index > 0);
      activeStepIndex = nextIncompleteStep === -1 ? totalSteps - 1 : nextIncompleteStep;
    }
  } else {
    // First step is NOT completed, stay on first step (index 0)
    activeStepIndex = 0;
  }

  return (
    <Grid item xs={12} md={12}>
      <Typography className="card-section-hedging">
        Application Progress
      </Typography>

      <ProgressBarContainer>
        {steps.map((step: StepData, index: number) => {
         
          // Special handling for first step
          // First step should ONLY be completed (ticked) when:
          // 1. isEligible is null/undefined AND isQualified is true
          let isCompleted;
          if (index === 0) {
            // Use the same logic as isFirstStepCompleted for the first step
            isCompleted = isFirstStepCompleted;
          } else {
            // For other steps (index > 0)
            // If eligibility check failed (cross in eligibility step), do not color/complete the second step
            if (steps[0]?.isEligible === false && index === 1) {
              isCompleted = false;
            } else if (!isFirstStepCompleted) {
              isCompleted = false;
            } else {
              isCompleted = step.status === "completed";
            }
          }
          // If previous step is completed, this step should be colored (active style), even if not completed
          let isActive = false;
          if (index === 0) {
            isActive = !isCompleted; // First step is active if not completed
          } else {
            // If eligibility check failed, do not color/activate the second step
            if (steps[0]?.isEligible === false && index === 1) {
              isActive = false;
            } else {
              const prevStep = steps[index - 1];
              if (prevStep && (prevStep.status === "completed")) {
                isActive = !isCompleted; // Next box is colored if previous is ticked and this is not completed
              }
            }
          }
          
           // const isActive = index === activeStepIndex;
           // const isStepActiveOrComplete = isCompleted || isActive;
          const showLine = index < totalSteps - 1;
          
          // Check if this is Additional Details step (index 3) or beyond
          const isAfterAdditionalDetails = index > 3;
          const isProvisionalNotGenerated = step.isProvisionalGenerated !== true;
          
          // Line completion logic:
            // For disbursed (all completed): all lines should be colored
            let isLineCompleted;
            // If the current step is completed, the line to the next step should be colored
            if (index === 0) {
              isLineCompleted = isFirstStepCompleted;
            } else {
              if (!isFirstStepCompleted) {
                isLineCompleted = false;
              } else {
                isLineCompleted = isCompleted;
              }
            }
          
          const isEligibleUser = step.isEligible;

          return (
            <StepWrapper key={index}>
              {showLine && (
                <ProgressBarLine
                  iscompleted={isLineCompleted ? "true" : "false"}
                  className="progress-bar-line"
                />
              )}

              <CustomStepIconNew
                index={index}
                isCompleted={isCompleted}
                isActive={isActive}
                isQualified={step.isQualified}
                isEligible={step.isEligible}
                appStatus={step.applicationStatus}
              />

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  textAlign: "left",
                  mt: 1,
                  maxWidth: "100%",
                }}
              >
                <Typography
                  fontWeight={600}
                  fontSize="0.7rem"
                  color={
                    (isFirstStepNotEligible || isFirstStepInProgress || isFirstStepNotQualifiedAndChecking) && index > 0 
                      ? "#66646dff" 
                      : (isCompleted && step.applicationStatus !== "application closed" && step.applicationStatus !== "application rejected" && step.applicationStatus !== "declined by credit" ? "#00b3b5" : "#66646dff")
                  }
                >
                  {step?.label}
                </Typography>
                {/* Completion rate display removed */}
              </Box>
            </StepWrapper>
          );
        })}
      </ProgressBarContainer>
    </Grid>
  );
};

// --- Mobile Accordion Component ---
const MobileAccordion: React.FC<ApplicationProgressContainerProps> = ({
  data,
  partnerStartedJourney,
}) => {
  const [isStepperExpanded, setIsStepperExpanded] = useState(false);

  // Check if first step is not eligible (false) or in-progress (null)
  const isFirstStepNotEligible = data[0]?.isEligible === false;
  const isFirstStepInProgress = data[0]?.isEligible === null;
  
  // Check if first step is qualified and eligibility check is null/undefined (should show tick and move to next stage)
  const isFirstStepQualifiedAndChecking = (data[0]?.isEligible === null || data[0]?.isEligible === undefined) && data[0]?.isQualified === true;
  const isFirstStepQualifiedAndCheckingOne = data[0]?.isEligible === true;
  
  // Check if first step is not qualified and eligibility check is null/undefined (should show "1" and stay on first stage)
  const isFirstStepNotQualifiedAndChecking = data[0]?.isQualified === false && (data[0]?.isEligible === null || data[0]?.isEligible === undefined);
  
  // Check if first step is completed (ticked)
  const isFirstStepCompleted = isFirstStepQualifiedAndChecking || isFirstStepQualifiedAndCheckingOne;

  // Determine the active step index for mobile
  let activeStepIndex = 0;
  if (isFirstStepCompleted) {
    if (data[0]?.isEligible === true) {
      activeStepIndex = 1;
    } else {
      const nextIncompleteStep = data.findIndex((step, index) => index > 0 );
      activeStepIndex = nextIncompleteStep === -1 ? data.length - 1 : nextIncompleteStep;
    }
  } else {
    activeStepIndex = 0;
  }

  return (
    <Box
      sx={{
        p: 1,
        border: "1px solid rgba(215, 216, 222, 1)",
        borderRadius: 2,
        mb: 2,
        mt: 2,
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        onClick={() => setIsStepperExpanded(!isStepperExpanded)}
        sx={{ cursor: "pointer" }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: "#333",
            fontSize: "15px",
          }}
        >
          Application Progress
        </Typography>
        <Box sx={{ pt: 1 }}>
          {isStepperExpanded ? (
            <KeyboardArrowUpIcon sx={{ color: "#00b3b5" }} />
          ) : (
            <KeyboardArrowDownIcon sx={{ color: "#00b3b5" }} />
          )}
        </Box>
      </Box>

      {isStepperExpanded && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            mt: 2,
          }}
        >
          {data.map((step, stepIndex) => {
            // Special handling for first step
            // If isEligible is null and isQualified is true, first step should be completed
            // If isQualified is false and isEligible is null, first step should NOT be completed
            let isCompleted;
            if (stepIndex === 0) {
              if ((step.isEligible === null || step.isEligible === undefined) && step.isQualified === true) {
                isCompleted = true; // Show tick
              } else if (step.isEligible === true) {
                isCompleted = true; // Show tick - eligibility checked
              } else if (step.isQualified === false && (step.isEligible === null || step.isEligible === undefined)) {
                isCompleted = false; // Show "1"
              } else if (step.isEligible === null) {
                isCompleted = false;
              } else {
                isCompleted = false;
              }
            } else {
              // For other steps (stepIndex > 0)
              // If eligibility check failed (cross in eligibility step), do not color/complete the second step
              if (data[0]?.isEligible === false && stepIndex === 1) {
                isCompleted = false;
              } else if (!isFirstStepCompleted) {
                isCompleted = false;
              } else {
                isCompleted = step.status === "completed";
              }
            }
            // If first step is completed (ticked), and this is step index 1, it should be active
            // If isEligible === true, second stage should be in progress
            const isActive = (() => {
              if (data[0]?.isEligible === false && stepIndex === 1) {
                return false;
              }
              if (!isFirstStepCompleted && stepIndex > 0) {
                return false;
              }
              // If isEligible === true, second stage is in progress
              if (data[0]?.isEligible === true && stepIndex === 1) {
                return true;
              }
              // If first step is completed and this is step index 1, it should be active
              if (isFirstStepCompleted && stepIndex === 1) {
                return true;
              }
              // Normal flow
              return stepIndex === 0 || (data[stepIndex - 1]?.status === "completed" && !isCompleted);
            })();
            
            return (
              <Box
                key={stepIndex}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <CustomStepIcon
                  icon={stepIndex + 1}
                  completed={isCompleted}
                  active={isActive}
                  totalSteps={data.length}
                  isEligible={step.isEligible}
                  isQualified={step.isQualified}
                  appStatus={step.applicationStatus}
                />

                <Box sx={{ flex: 1, position: "relative" }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: (isFirstStepNotEligible || isFirstStepInProgress || isFirstStepNotQualifiedAndChecking) && stepIndex > 0 
                        ? "#66646dff" 
                        : (isCompleted && step.applicationStatus !== "application closed" && step.applicationStatus !== "application rejected" && step.applicationStatus !== "declined by credit"? "#00b3b5" : "#333"),
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    {step.label}
                  </Typography>
                  {/* Completion rate display removed */}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

const ApplicationProgressContainer: React.FC<
  ApplicationProgressContainerProps
> = (props) => {
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down("sm"));

  if (props.isLoading) {
    return isMobileView ? (
      <MobileAccordionSkeleton />
    ) : (
      <DesktopStepperSkeleton />
    );
  }

  return isMobileView ? (
    <MobileAccordion {...props} />
  ) : (
    <DesktopStepper {...props} />
  );
};

export default ApplicationProgressContainer;
